import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { API } from "@/lib/admin-constants";
import { Plus, Trash2, Loader2, Film, Pencil, Eye, Share2, Play } from "lucide-react";

const EVENT_TYPES = [
  { id: "goal", label: "Goal" },
  { id: "assist", label: "Assist" },
  { id: "save", label: "Save" },
  { id: "tackle", label: "Tackle" },
  { id: "highlight", label: "Highlight" },
  { id: "full_game", label: "Full Game" },
];

const HighlightsManager = () => {
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", video_url: "", player_names: "", event_type: "highlight",
    game_info: "", description: "", thumbnail_url: "", is_public: true,
  });

  const fetchHighlights = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/highlights`, { withCredentials: true });
      setHighlights(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchHighlights(); }, [fetchHighlights]);

  const resetForm = () => {
    setForm({ title: "", video_url: "", player_names: "", event_type: "highlight", game_info: "", description: "", thumbnail_url: "", is_public: true });
    setEditing(null);
  };

  const openEdit = (hl) => {
    setEditing(hl);
    setForm({
      title: hl.title, video_url: hl.video_url, player_names: (hl.player_names || []).join(", "),
      event_type: hl.event_type || "highlight", game_info: hl.game_info || "",
      description: hl.description || "", thumbnail_url: hl.thumbnail_url || "", is_public: hl.is_public !== false,
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.video_url.trim()) { toast.error("Title and video URL required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      player_names: form.player_names ? form.player_names.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    try {
      if (editing) {
        await axios.put(`${API}/highlights/${editing.highlight_id}`, payload, { withCredentials: true });
        toast.success("Highlight updated");
      } else {
        await axios.post(`${API}/highlights`, payload, { withCredentials: true });
        toast.success("Highlight added");
      }
      setSheetOpen(false); resetForm(); fetchHighlights();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this highlight?")) return;
    try {
      await axios.delete(`${API}/highlights/${id}`, { withCredentials: true });
      toast.success("Deleted"); fetchHighlights();
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-cfc-primary" size={32} /></div>;

  const totalViews = highlights.reduce((sum, h) => sum + (h.views || 0), 0);
  const totalShares = highlights.reduce((sum, h) => sum + (h.shares || 0), 0);

  return (
    <div data-testid="highlights-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">Player Highlights</h1>
          <p className="text-sm text-cfc-text-muted mt-1">{highlights.length} clips &middot; {totalViews} views &middot; {totalShares} shares</p>
        </div>
        <Button className="bg-cfc-primary text-white" onClick={() => { resetForm(); setSheetOpen(true); }} data-testid="add-highlight-btn">
          <Plus size={16} className="mr-1.5" /> Add Highlight
        </Button>
      </div>

      <p className="text-sm text-cfc-text-muted bg-cfc-cream-alt border border-cfc-border p-3 rounded-sm mb-6">
        Add YouTube, Vimeo, or direct video links. Tag players by name (comma-separated) to auto-generate shareable player reel pages at <strong>/highlights/player/Name</strong>.
      </p>

      {highlights.length === 0 ? (
        <Card className="border-cfc-border">
          <CardContent className="flex flex-col items-center py-16 text-cfc-text-muted">
            <Film size={48} className="mb-4 opacity-40" />
            <p className="text-lg font-medium mb-1">No highlights yet</p>
            <p className="text-sm mb-4">Add game clips and tag players to build shareable highlight reels</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {highlights.map((hl) => (
            <div key={hl.highlight_id} className="flex items-center gap-4 bg-white border border-cfc-border rounded-sm p-3" data-testid={`highlight-row-${hl.highlight_id}`}>
              <div className="w-20 h-12 bg-cfc-primary/10 rounded overflow-hidden flex-shrink-0 flex items-center justify-center">
                {hl.thumbnail_url ? (
                  <img src={hl.thumbnail_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Play size={18} className="text-cfc-primary/30" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-sm text-cfc-primary truncate">{hl.title}</h3>
                  <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-cfc-cream-alt text-cfc-text-muted border border-cfc-border">{hl.event_type}</span>
                  {!hl.is_public && <span className="text-[9px] text-red-500 font-bold">HIDDEN</span>}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-cfc-text-muted mt-0.5">
                  {hl.player_names?.length > 0 && <span>{hl.player_names.join(", ")}</span>}
                  {hl.game_info && <span>&middot; {hl.game_info}</span>}
                  <span className="flex items-center gap-0.5"><Eye size={10} /> {hl.views || 0}</span>
                  <span className="flex items-center gap-0.5"><Share2 size={10} /> {hl.shares || 0}</span>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => openEdit(hl)}><Pencil size={14} /></Button>
              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(hl.highlight_id)}><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
      )}

      {/* Form Sheet */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { setSheetOpen(open); if (!open) resetForm(); }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading text-cfc-primary">{editing ? "Edit Highlight" : "Add Highlight"}</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-3">
            <div><label className="text-xs font-bold text-cfc-text-muted uppercase">Title *</label><Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="e.g. Amazing goal by Jake vs Skyline" className="mt-1" data-testid="hl-title" /></div>
            <div><label className="text-xs font-bold text-cfc-text-muted uppercase">Video URL *</label><Input value={form.video_url} onChange={(e) => setForm((p) => ({ ...p, video_url: e.target.value }))} placeholder="YouTube, Vimeo, or direct video link" className="mt-1" data-testid="hl-url" /><p className="text-[10px] text-cfc-text-muted mt-1">Supports YouTube, Vimeo, and direct video file URLs</p></div>
            <div><label className="text-xs font-bold text-cfc-text-muted uppercase">Tagged Players (comma separated)</label><Input value={form.player_names} onChange={(e) => setForm((p) => ({ ...p, player_names: e.target.value }))} placeholder="e.g. Jake Smith, Maria Johnson" className="mt-1" data-testid="hl-players" /></div>
            <div>
              <label className="text-xs font-bold text-cfc-text-muted uppercase">Event Type</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {EVENT_TYPES.map((t) => (
                  <button key={t.id} onClick={() => setForm((p) => ({ ...p, event_type: t.id }))} className={`px-2.5 py-1 text-[11px] font-bold rounded-sm border ${form.event_type === t.id ? "bg-cfc-primary text-white border-cfc-primary" : "border-cfc-border text-cfc-text-muted"}`} data-testid={`hl-type-${t.id}`}>{t.label}</button>
                ))}
              </div>
            </div>
            <div><label className="text-xs font-bold text-cfc-text-muted uppercase">Game Info</label><Input value={form.game_info} onChange={(e) => setForm((p) => ({ ...p, game_info: e.target.value }))} placeholder="e.g. vs Skyline FC - Oct 15" className="mt-1" data-testid="hl-game" /></div>
            <div><label className="text-xs font-bold text-cfc-text-muted uppercase">Description</label><Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} className="mt-1" data-testid="hl-desc" /></div>
            <div><label className="text-xs font-bold text-cfc-text-muted uppercase">Thumbnail URL</label><Input value={form.thumbnail_url} onChange={(e) => setForm((p) => ({ ...p, thumbnail_url: e.target.value }))} placeholder="Image URL for preview" className="mt-1" data-testid="hl-thumb" /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_public} onChange={(e) => setForm((p) => ({ ...p, is_public: e.target.checked }))} className="accent-cfc-primary" />
              <label className="text-sm text-cfc-primary font-medium">Show publicly</label>
            </div>
            <Separator />
            <Button className="w-full bg-cfc-primary text-white" onClick={handleSave} disabled={saving} data-testid="save-highlight-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} {editing ? "Update" : "Add"} Highlight
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default HighlightsManager;
